/**
 * @fileoverview Pinia Store Configuration for KeyReply Webchat
 *
 * This file configures and exports the Pinia store instance for the Vue 3 webchat widget.
 * Pinia is the recommended state management solution for Vue 3, offering:
 * - Full TypeScript support with type inference
 * - Simplified API without mutations (direct state changes)
 * - DevTools integration
 * - Hot module replacement
 * - Plugin system
 *
 * @module stores
 * @author KeyReply Engineering Team
 *
 * @example Usage in main.ts:
 * ```typescript
 * import { createApp } from 'vue'
 * import { pinia } from './stores'
 * import App from './App.vue'
 *
 * const app = createApp(App)
 * app.use(pinia)
 * app.mount('#app')
 * ```
 *
 * @example Usage in components:
 * ```typescript
 * import { useMainStore } from '@/stores/main'
 *
 * // In setup() or <script setup>
 * const store = useMainStore()
 *
 * // Access state
 * console.log(store.messages)
 *
 * // Call actions
 * await store.sendMessage({ text: 'Hello' })
 *
 * // Access getters
 * console.log(store.stickyMenuText)
 * ```
 */

import { createPinia } from 'pinia'

/**
 * Pinia store instance
 *
 * Single source of truth for all webchat widget state.
 * This instance should be installed on the Vue app during initialization.
 */
export const pinia = createPinia()

// Re-export the main store for convenience
export { useMainStore } from './main'

export default pinia
